import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { TodoPersonalSectionResponse } from '@/lib/api';

const NO_SECTION = 'none';

interface CreatePersonalItemDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: (input: {
    personalSectionId: string | null;
    title: string;
    description?: string;
  }) => void;
  sections: TodoPersonalSectionResponse[];
  /** Pre-select a section (e.g. when adding from a specific column). */
  defaultSectionId?: string | null;
}

/**
 * Add a private item to the personal "My Tasks" board. Mirrors the shared
 * list's CreateItemDialog (title + description + section picker) so adding
 * items feels the same in both views. Personal items are private-only.
 */
export function CreatePersonalItemDialog({
  open,
  onClose,
  onCreate,
  sections,
  defaultSectionId,
}: CreatePersonalItemDialogProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [sectionId, setSectionId] = useState<string>(NO_SECTION);

  useEffect(() => {
    if (open) setSectionId(defaultSectionId ?? NO_SECTION);
  }, [open, defaultSectionId]);

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) onClose();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = title.trim();
    if (!trimmed) return;
    onCreate({
      personalSectionId: sectionId !== NO_SECTION ? sectionId : null,
      title: trimmed,
      description: description.trim() || undefined,
    });
    setTitle('');
    setDescription('');
    setSectionId(NO_SECTION);
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Item</DialogTitle>
          <DialogDescription>Create a private item only you can see.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="personal-item-title">Title</Label>
            <Input
              id="personal-item-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What needs to be done?"
              required
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="personal-item-description">Description (optional)</Label>
            <Textarea
              id="personal-item-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add details..."
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label>Section</Label>
            <Select value={sectionId} onValueChange={setSectionId}>
              <SelectTrigger>
                <SelectValue placeholder="No section" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_SECTION}>No section</SelectItem>
                {sections.map((s) => (
                  <SelectItem key={s.personalSectionId} value={s.personalSectionId}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!title.trim()}>
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
